import { BehaviorSubject, Observable } from 'rxjs'

import {
  PlatformComponents,
  PlatformConfigurationKeys,
  PlatformModules,
  PlatformScopes,
} from '@flowx/designer/app.constants'
import { AiPermissions } from '@flowx/designer/modules/ai/types/ai.types'
import { EnvironmentInfo } from '@flowx/designer/shared/api/platform-status.api'

export type Scopes = Record<PlatformScopes, boolean>

export interface IConfig {
  accessRights: IAccessRights
}

export interface IEnvironmentConfiguration {
  [PlatformComponents.APPLICATION_MANAGER]: {
    [PlatformConfigurationKeys.MAX_FILE_SIZE]: string
  }
  [PlatformComponents.CMS]: {
    [PlatformConfigurationKeys.MAX_FILE_SIZE]: string
  }
  [PlatformComponents.RUNTIME_MANAGER]: {
    [PlatformConfigurationKeys.MAX_FILE_SIZE]: string
  }
}

const initialEnvironmentConfiguration: IEnvironmentConfiguration = {
  [PlatformComponents.APPLICATION_MANAGER]: {
    [PlatformConfigurationKeys.MAX_FILE_SIZE]: '10MB',
  },
  [PlatformComponents.CMS]: {
    [PlatformConfigurationKeys.MAX_FILE_SIZE]: '10MB',
  },
  [PlatformComponents.RUNTIME_MANAGER]: {
    [PlatformConfigurationKeys.MAX_FILE_SIZE]: '10MB',
  },
}

export interface IAccessRights {
  [PlatformComponents.APPLICATION_MANAGER]: {
    [PlatformModules.APPLICATIONS]: Scopes
    [PlatformModules.BUILDS]: Scopes
    [PlatformModules.ACTIVE_POLICY]: Scopes
    [PlatformModules.CONFIGS]: Scopes
    [PlatformModules.CONFIG_OVERRIDES]: Scopes
    [PlatformModules.DEPENDENCIES]: Scopes
  }
  [PlatformComponents.ADMIN]: {
    [PlatformModules.DEFINITIONS]: Scopes
    [PlatformModules.PLATFORM]: Scopes
    [PlatformModules.USERS]: Scopes
    [PlatformModules.GENERAL_CONFIG]: Scopes
    [PlatformModules.INTEGRATIONS]: Scopes
    [PlatformModules.PROJECT_DATA_TYPES]: Scopes
  }
  [PlatformComponents.NOTIFICATIONS]: {
    [PlatformModules.NOTIFICATIONS_TEMPLATES]: Scopes
  }
  [PlatformComponents.DOCUMENT]: {
    [PlatformModules.DOCUMENT_TEMPLATES]: Scopes
  }
  [PlatformComponents.CMS]: {
    [PlatformModules.CONTENTS]: Scopes
    [PlatformModules.TAXONOMIES]: Scopes
    [PlatformModules.MEDIA_LIBRARY]: Scopes
    [PlatformModules.THEME]: Scopes
  }
  [PlatformComponents.ENGINE]: {
    [PlatformModules.INSTANCES]: Scopes
    [PlatformModules.DEFINITIONS]: Scopes
  }
  [PlatformComponents.TASK_MANAGEMENT]: {
    [PlatformModules.TASKS]: Scopes
    [PlatformModules.VIEW]: Scopes
    [PlatformModules.HOOKS]: Scopes
    [PlatformModules.PROCESS_ALLOCATIONS]: Scopes
    [PlatformModules.OUT_OF_OFFICE]: Scopes
  }
  [PlatformComponents.LICENCE]: {
    [PlatformModules.LICENCE]: Scopes
  }
  [PlatformComponents.AI_ASSISTANT]: { [PlatformModules.AI_ASSISTANT]: Scopes }
  [PlatformComponents.AI_ANALYST]: { [PlatformModules.AI_ANALYST]: Scopes }
  [PlatformComponents.AI_DESIGNER]: { [PlatformModules.AI_DESIGNER]: Scopes }
  [PlatformComponents.AI_DEVELOPER]: { [PlatformModules.AI_DEVELOPER]: Scopes }
  [PlatformComponents.INTEGRATION_DESIGNER]: {
    [PlatformModules.ID_SYSTEMS]: Scopes
    [PlatformModules.ID_WORKFLOWS]: Scopes
  }
}

const initialAccessRights: IAccessRights = {
  [PlatformComponents.APPLICATION_MANAGER]: {
    [PlatformModules.APPLICATIONS]: getInitialScopes(),
    [PlatformModules.BUILDS]: getInitialScopes(),
    [PlatformModules.ACTIVE_POLICY]: getInitialScopes(),
    [PlatformModules.CONFIGS]: getInitialScopes(),
    [PlatformModules.CONFIG_OVERRIDES]: getInitialScopes(),
    [PlatformModules.DEPENDENCIES]: getInitialScopes(),
  },
  [PlatformComponents.ADMIN]: {
    [PlatformModules.DEFINITIONS]: getInitialScopes(),
    [PlatformModules.PLATFORM]: getInitialScopes(),
    [PlatformModules.USERS]: getInitialScopes(),
    [PlatformModules.GENERAL_CONFIG]: getInitialScopes(),
    [PlatformModules.INTEGRATIONS]: getInitialScopes(),
    [PlatformModules.PROJECT_DATA_TYPES]: getInitialScopes(),
  },
  [PlatformComponents.ENGINE]: {
    [PlatformModules.DEFINITIONS]: getInitialScopes(),
    [PlatformModules.INSTANCES]: getInitialScopes(),
  },
  [PlatformComponents.NOTIFICATIONS]: {
    [PlatformModules.NOTIFICATIONS_TEMPLATES]: getInitialScopes(),
  },
  [PlatformComponents.DOCUMENT]: {
    [PlatformModules.DOCUMENT_TEMPLATES]: getInitialScopes(),
  },
  [PlatformComponents.CMS]: {
    [PlatformModules.CONTENTS]: getInitialScopes(),
    [PlatformModules.TAXONOMIES]: getInitialScopes(),
    [PlatformModules.MEDIA_LIBRARY]: getInitialScopes(),
    [PlatformModules.THEME]: getInitialScopes(),
  },
  [PlatformComponents.TASK_MANAGEMENT]: {
    [PlatformModules.TASKS]: getInitialScopes(),
    [PlatformModules.HOOKS]: getInitialScopes(),
    [PlatformModules.VIEW]: getInitialScopes(),
    [PlatformModules.PROCESS_ALLOCATIONS]: getInitialScopes(),
    [PlatformModules.OUT_OF_OFFICE]: getInitialScopes(),
  },
  [PlatformComponents.LICENCE]: {
    [PlatformModules.LICENCE]: getInitialScopes(),
  },
  [PlatformComponents.AI_ASSISTANT]: { [PlatformModules.AI_ASSISTANT]: getInitialScopes() },
  [PlatformComponents.AI_ANALYST]: { [PlatformModules.AI_ANALYST]: getInitialScopes() },
  [PlatformComponents.AI_DESIGNER]: { [PlatformModules.AI_DESIGNER]: getInitialScopes() },
  [PlatformComponents.AI_DEVELOPER]: { [PlatformModules.AI_DEVELOPER]: getInitialScopes() },
  [PlatformComponents.INTEGRATION_DESIGNER]: {
    [PlatformModules.ID_SYSTEMS]: getInitialScopes(),
    [PlatformModules.ID_WORKFLOWS]: getInitialScopes(),
  },
}

export enum Environments {
  DEV = 'DEV',
  UAT = 'UAT',
  PRODUCTION = 'PRODUCTION',
  TEST = 'TEST',
  PRE_PRODUCTION = 'PRE_PRODUCTION',
  OTHER = 'OTHER',
}

export const predefinedEnvironments = [
  { value: Environments.DEV, label: 'DEV' },
  { value: Environments.UAT, label: 'UAT' },
  { value: Environments.PRODUCTION, label: 'PRODUCTION' },
  { value: Environments.TEST, label: 'TEST' },
  { value: Environments.PRE_PRODUCTION, label: 'PRE-PRODUCTION' },
]

function getInitialScopes(): Scopes {
  return Object.values(PlatformScopes).reduce(
    (scopes, scope) => ({ ...scopes, [scope]: false }),
    {} as Scopes
  )
}

const _permissions$: BehaviorSubject<IConfig> = new BehaviorSubject({
  accessRights: null,
})
const _accessRights$: BehaviorSubject<IAccessRights | Record<string, never>> = new BehaviorSubject(
  {}
)
const _environmentDetails$: BehaviorSubject<EnvironmentInfo> = new BehaviorSubject(null)
const _environmentConfiguration$: BehaviorSubject<IEnvironmentConfiguration> = new BehaviorSubject(
  null
)

export function setPermissions(accessRights: IAccessRights): void {
  _accessRights$.next(transformPermissions(accessRights))
}

export function setEnvironmentDetails(envDetails: EnvironmentInfo): void {
  _environmentDetails$.next(envDetails)
}

export function setEnvironmentConfiguration(envConfiguration: IEnvironmentConfiguration): void {
  _environmentConfiguration$.next({
    ...initialEnvironmentConfiguration,
    ...envConfiguration,
  })
}

export function getPermissions$(): Observable<any> {
  return _permissions$.asObservable()
}

export function getAccessRights(): IAccessRights | Record<string, never> {
  return _accessRights$.value
}

export function getEnvironmentDetails$(): Observable<EnvironmentInfo> {
  return _environmentDetails$
}

export function getEnvironmentDetails(): EnvironmentInfo {
  return _environmentDetails$.value
}

export function getEnvironmentConfiguration$(): Observable<IEnvironmentConfiguration> {
  return _environmentConfiguration$
}

export function getEnvironmentConfiguration(): IEnvironmentConfiguration {
  return _environmentConfiguration$.value
}

export function transformPermissions(accessRights: IAccessRights): IAccessRights {
  return {
    ...initialAccessRights,
    ...Object.keys(initialAccessRights).reduce(
      (components, componentName) => ({
        ...components,
        [componentName]: Object.keys(initialAccessRights[componentName]).reduce(
          (modules, moduleName) => ({
            ...modules,
            [moduleName]: Object.values(PlatformScopes).reduce(
              (scopes, scope) => ({
                ...scopes,
                [scope]: Boolean(
                  accessRights[componentName] &&
                    accessRights[componentName][moduleName] &&
                    accessRights[componentName][moduleName].includes(scope)
                ),
              }),
              {}
            ),
          }),
          {}
        ),
      }),
      {}
    ),
  }
}

export function usersPermissions(): Record<string, boolean> {
  const accessRights = getAccessRights()
  return {
    showChangeUserStatus:
      accessRights[PlatformComponents.ADMIN][PlatformModules.USERS][PlatformScopes.EDIT],
  }
}

export function sidenavPermissions(): Record<string, boolean> {
  const accessRights = getAccessRights()
  return {
    showApps:
      accessRights[PlatformComponents.APPLICATION_MANAGER][PlatformModules.APPLICATIONS][
        PlatformScopes.READ
      ],
    showProcesses:
      accessRights[PlatformComponents.ADMIN][PlatformModules.DEFINITIONS][PlatformScopes.READ],

    showProjectDataTypes:
      accessRights[PlatformComponents.ADMIN][PlatformModules.PROJECT_DATA_TYPES][
        PlatformScopes.READ
      ],
    showActiveProcesses:
      accessRights[PlatformComponents.ENGINE][PlatformModules.INSTANCES][PlatformScopes.READ],

    showDesignAssets:
      accessRights[PlatformComponents.CMS][PlatformModules.THEME][PlatformScopes.READ] ||
      accessRights[PlatformComponents.CMS][PlatformModules.MEDIA_LIBRARY][PlatformScopes.READ],

    showCms:
      accessRights[PlatformComponents.CMS][PlatformModules.THEME][PlatformScopes.READ] ||
      accessRights[PlatformComponents.CMS][PlatformModules.MEDIA_LIBRARY][PlatformScopes.READ],

    showPrivateDocuments:
      accessRights[PlatformComponents.CMS][PlatformModules.MEDIA_LIBRARY][PlatformScopes.READ],

    showContent:
      accessRights[PlatformComponents.CMS][PlatformModules.CONTENTS][PlatformScopes.READ],

    showFontFiles: accessRights[PlatformComponents.CMS][PlatformModules.THEME][PlatformScopes.READ],

    showThemes: accessRights[PlatformComponents.CMS][PlatformModules.THEME][PlatformScopes.READ],
    showMediaLibrary:
      accessRights[PlatformComponents.CMS][PlatformModules.MEDIA_LIBRARY][PlatformScopes.READ],

    showPlugins:
      accessRights[PlatformComponents.ADMIN][PlatformModules.DEFINITIONS][PlatformScopes.READ] ||
      accessRights[PlatformComponents.TASK_MANAGEMENT][PlatformModules.TASKS][
        PlatformScopes.READ
      ] ||
      accessRights[PlatformComponents.NOTIFICATIONS][PlatformModules.NOTIFICATIONS_TEMPLATES][
        PlatformScopes.READ
      ] ||
      accessRights[PlatformComponents.DOCUMENT][PlatformModules.DOCUMENT_TEMPLATES][
        PlatformScopes.READ
      ] ||
      accessRights[PlatformComponents.TASK_MANAGEMENT][PlatformModules.HOOKS][
        PlatformScopes.READ
      ] ||
      accessRights[PlatformComponents.TASK_MANAGEMENT][PlatformModules.OUT_OF_OFFICE][
        PlatformScopes.READ
      ] ||
      accessRights[PlatformComponents.TASK_MANAGEMENT][PlatformModules.PROCESS_ALLOCATIONS][
        PlatformScopes.READ
      ],
    showTaxonomies:
      accessRights[PlatformComponents.CMS][PlatformModules.TAXONOMIES][PlatformScopes.READ],

    showTaskManager:
      accessRights[PlatformComponents.TASK_MANAGEMENT][PlatformModules.VIEW][PlatformScopes.READ] ||
      accessRights[PlatformComponents.TASK_MANAGEMENT][PlatformModules.HOOKS][
        PlatformScopes.READ
      ] ||
      accessRights[PlatformComponents.ADMIN][PlatformModules.DEFINITIONS][PlatformScopes.READ] ||
      accessRights[PlatformComponents.TASK_MANAGEMENT][PlatformModules.PROCESS_ALLOCATIONS][
        PlatformScopes.READ
      ],

    showAllTasks:
      accessRights[PlatformComponents.TASK_MANAGEMENT][PlatformModules.TASKS][PlatformScopes.READ],

    showHooks:
      accessRights[PlatformComponents.TASK_MANAGEMENT][PlatformModules.HOOKS][PlatformScopes.READ],
    showViews:
      accessRights[PlatformComponents.TASK_MANAGEMENT][PlatformModules.VIEW][PlatformScopes.READ],

    showNotifications:
      accessRights[PlatformComponents.NOTIFICATIONS][PlatformModules.NOTIFICATIONS_TEMPLATES][
        PlatformScopes.READ
      ],
    showDocuments:
      accessRights[PlatformComponents.DOCUMENT][PlatformModules.DOCUMENT_TEMPLATES][
        PlatformScopes.READ
      ],

    showAccessManagement:
      accessRights[PlatformComponents.ADMIN][PlatformModules.USERS][PlatformScopes.READ],

    showLicence:
      accessRights[PlatformComponents.LICENCE][PlatformModules.LICENCE][PlatformScopes.READ] ||
      accessRights[PlatformComponents.LICENCE][PlatformModules.LICENCE][PlatformScopes.EDIT],

    showPlatformStatus:
      accessRights[PlatformComponents.ADMIN][PlatformModules.PLATFORM][PlatformScopes.READ],

    showIntegrations:
      accessRights[PlatformComponents.ADMIN][PlatformModules.INTEGRATIONS][PlatformScopes.READ],

    showProcessAllocations:
      accessRights[PlatformComponents.TASK_MANAGEMENT][PlatformModules.PROCESS_ALLOCATIONS][
        PlatformScopes.READ
      ],

    showOutOfOffice:
      accessRights[PlatformComponents.TASK_MANAGEMENT][PlatformModules.OUT_OF_OFFICE][
        PlatformScopes.READ
      ],

    showIntegrationDesigner:
      accessRights[PlatformComponents.INTEGRATION_DESIGNER][PlatformModules.ID_SYSTEMS][
        PlatformScopes.READ
      ] ||
      accessRights[PlatformComponents.INTEGRATION_DESIGNER][PlatformModules.ID_WORKFLOWS][
        PlatformScopes.READ
      ] ||
      accessRights[PlatformComponents.INTEGRATION_DESIGNER][PlatformModules.ID_WORKFLOWS][
        PlatformScopes.READ_RESTRICTED
      ],
    showIDSystems:
      accessRights[PlatformComponents.INTEGRATION_DESIGNER][PlatformModules.ID_SYSTEMS][
        PlatformScopes.READ
      ],
    showIDWorkflows:
      accessRights[PlatformComponents.INTEGRATION_DESIGNER][PlatformModules.ID_WORKFLOWS][
        PlatformScopes.READ
      ] ||
      accessRights[PlatformComponents.INTEGRATION_DESIGNER][PlatformModules.ID_WORKFLOWS][
        PlatformScopes.READ_RESTRICTED
      ],
    showDependencies:
      accessRights[PlatformComponents.APPLICATION_MANAGER][PlatformModules.DEPENDENCIES][
        PlatformScopes.READ
      ],
    showConfigurationParameters:
      accessRights[PlatformComponents.APPLICATION_MANAGER][PlatformModules.CONFIGS][
        PlatformScopes.READ
      ],
    // RUNTIME
    showBuilds:
      accessRights[PlatformComponents.APPLICATION_MANAGER][PlatformModules.BUILDS][
        PlatformScopes.READ
      ],
    showActivePolicy:
      accessRights[PlatformComponents.APPLICATION_MANAGER][PlatformModules.ACTIVE_POLICY][
        PlatformScopes.READ
      ],
    showOverrides:
      accessRights[PlatformComponents.APPLICATION_MANAGER][PlatformModules.CONFIG_OVERRIDES][
        PlatformScopes.READ
      ],
  }
}

export function aiPermissions(): AiPermissions {
  const accessRights = getAccessRights()
  const mediaLibraryPermissions =
    accessRights[PlatformComponents.CMS][PlatformModules.MEDIA_LIBRARY]

  const showAssistantAgent =
    accessRights[PlatformComponents.AI_ASSISTANT][PlatformModules.AI_ASSISTANT][PlatformScopes.READ]
  const showAnalystAgent =
    accessRights[PlatformComponents.AI_ANALYST][PlatformModules.AI_ANALYST][PlatformScopes.EDIT]
  const showDesignerAgent =
    accessRights[PlatformComponents.AI_DESIGNER][PlatformModules.AI_DESIGNER][PlatformScopes.EDIT]
  const showDeveloperAgent =
    accessRights[PlatformComponents.AI_DEVELOPER][PlatformModules.AI_DEVELOPER][PlatformScopes.EDIT]
  const canUploadDocuments =
    mediaLibraryPermissions[PlatformScopes.EDIT] ||
    mediaLibraryPermissions[PlatformScopes.IMPORT] ||
    mediaLibraryPermissions[PlatformScopes.ADMIN]
  const canViewDocuments = mediaLibraryPermissions[PlatformScopes.READ] || canUploadDocuments

  return {
    showAssistantAgent,
    showAnalystAgent,
    showDesignerAgent,
    showDeveloperAgent,
    canViewDocuments,
    canUploadDocuments,
  }
}
